import { z } from "zod"
import { ChannelEnum } from "../campaigns/commonTypes"
import { TicketStatusEnum } from "../conversations/response_data"
import { ClusterDomainEnum } from "../insights/response_data"
import { createFilterBySchema, DateRangeSchema, oneOrMany } from "../shared/filter_dtos"
import { PaginationSchema } from "../shared/pagination_dtos"
import { QuerySchema } from "../shared/query_dtos"
import { createSortSchema } from "../shared/sort_dtos"
import { IssueStatusEnum, IssueUrgencyEnum } from "./response_data"

export const SupportPredefinedCategoryEnum = [
  "Order Tracking",
  "Order Changes",
  "Order Cancellation",
  "Returns & Refunds",
  "Delivery Issues",
  "Discounts & Promotions",
  "Size & Fit",
  "Stock & Availability",
  "Product Information",
  "Contact & Support",
  "Physical Stores & Pickup",
  "Invoices & Documentation",
  "Account & Access",
  "Payments & Financing",
  "Partnerships",
] as const

export const SupportCategorySchema = z.enum(SupportPredefinedCategoryEnum)

export const SupportPredefinedCategoryTree = {
  "Order Management": [
    "Order Tracking",
    "Order Changes",
    "Order Cancellation",
    "Order Preparation",
    "Order Fulfillment",
    "Replacements",
  ],
  "Shipping & Delivery": [
    "Delivery Updates",
    "Delivery Instructions",
    "Shipping Policies",
    "Shipping Delays",
    "Shipment Incidents",
    "Delivery Attempt",
    "Refused Delivery",
    "Customs Clearance",
  ],
  Returns: [
    "Return Status",
    "Return Policy",
    "Return Request",
    "Return Logistics",
    "Return Cancellation",
  ],
  Exchanges: ["Exchange Eligibility & Policy", "Exchange Request & Status", "Exchange Logistics"],
  Refunds: ["Refund Status", "Refund Request"],
  "Promotions & Loyalty": ["Discounts & Promotions", "Gift Cards", "Rewards & Loyalty"],
  Products: [
    "Size & Fit",
    "Stock & Availability",
    "Product Details",
    "Product Recommendations",
    "Product Warranty & Defects",
  ],
  Support: ["Contact & Support"],
  Stores: ["Physical Stores & Pickup"],
  "Invoices & Documents": ["Invoices & Documentation"],
  Account: [
    "Login & Access",
    "Registration",
    "Account Changes",
    "Account Privacy & Preferences",
    "Subscription Management",
    "Account Verification",
    "Account Technical Issues",
  ],
  Payments: ["Payment Methods", "Financing", "Payment Disputes & Chargebacks", "Payment Issues"],
  Partnerships: ["Partnerships"],
} as const

type SupportPredefinedCategoryTreePath = {
  [
    Category in keyof typeof SupportPredefinedCategoryTree & string
  ]: `${Category}/${(typeof SupportPredefinedCategoryTree)[Category][number] & string}`
}[keyof typeof SupportPredefinedCategoryTree & string]

export const SupportPredefinedCategoryTreeEnum = Object.entries(
  SupportPredefinedCategoryTree,
).flatMap(([category, subcategories]) =>
  subcategories.map((subcategory) => `${category}/${subcategory}`),
) as [SupportPredefinedCategoryTreePath, ...SupportPredefinedCategoryTreePath[]]

export const SupportCategoryTreeSchema = z.enum(SupportPredefinedCategoryTreeEnum)

type SupportPredefinedSubcategory =
  (typeof SupportPredefinedCategoryTree)[keyof typeof SupportPredefinedCategoryTree][number]

export const SupportPredefinedSubcategoryEnum = Object.values(
  SupportPredefinedCategoryTree,
).flat() as [SupportPredefinedSubcategory, ...SupportPredefinedSubcategory[]]

export const SupportSubcategorySchema = z.enum(SupportPredefinedSubcategoryEnum)

export const SUPPORT_CATEGORY_TAXONOMY = SupportPredefinedCategoryEnum.map(
  (category) => `- ${category}`,
).join("\n")

export const SUPPORT_CATEGORY_TREE_TAXONOMY = Object.entries(SupportPredefinedCategoryTree)
  .map(([category, subcategories]) => `- ${category}: ${subcategories.join("; ")}`)
  .join("\n")

export const SUPPORT_SUBCATEGORY_TAXONOMY = SupportPredefinedSubcategoryEnum.map(
  (category) => `- ${category}`,
).join("\n")

export const SupportFilterBy = createFilterBySchema([
  {
    key: "conversationId",
    value: z.string().optional(),
  } as const,
  {
    key: "domain",
    value: z.enum(ClusterDomainEnum).optional(),
  } as const,
  {
    key: "canonicalId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "segmentId",
    value: oneOrMany(z.string()),
  } as const,
  {
    key: "dateRange",
    value: DateRangeSchema,
  } as const,
  {
    key: "status",
    value: oneOrMany(z.enum(TicketStatusEnum)),
  } as const,
  {
    key: "humanReply",
    value: z.enum(["yes", "no"]).optional(),
  } as const,
  {
    key: "unansweredCustomerReply",
    value: z.literal("yes").optional(),
  } as const,
  {
    key: "issueStatus",
    value: oneOrMany(z.enum(IssueStatusEnum)),
  } as const,
  {
    key: "urgency",
    value: z.enum(IssueUrgencyEnum).optional(),
  } as const,
  {
    key: "customerId",
    value: z.string().optional(),
  } as const,
  {
    key: "campaignId",
    value: oneOrMany(z.string()),
  } as const,
  // Rollout boundaries for the new support flow (issues after the flag, tickets before it)
  {
    key: "createdFrom",
    value: z.string().datetime().optional(),
  } as const,
  {
    key: "createdBefore",
    value: z.string().datetime().optional(),
  } as const,
  {
    key: "channel",
    value: oneOrMany(z.enum(ChannelEnum)),
  } as const,
])

export const SupportSortFields = ["createdAt"] as const
export const SupportSortBy = createSortSchema(SupportSortFields, "createdAt", "desc")

export const SupportSchema = PaginationSchema.extend(SupportFilterBy.shape)
  .extend(SupportSortBy.shape)
  .extend(QuerySchema.shape)

export const SupportConversationTicketIdSchema = z.object({
  id: z.string().transform(BigInt),
})

export const SupportIssueIdSchema = z.object({
  id: z.string().transform(BigInt),
})

export const SupportUpdateConversationTicketStatusSchema = z.object({
  status: z.enum(TicketStatusEnum),
})

export const SupportUpdateIssueResolutionSchema = z.object({
  resolved: z.boolean(),
  closingNote: z.string().trim().optional(),
})

export const GetCustomerIssuesSchema = z.object({
  filterBy: z.object({
    customerId: z.string().min(1),
  }),
})

export const GetConversationSupportEntitiesSchema = z.object({
  conversationId: z.union([z.string(), z.number()]).transform(String),
})
